// Enhanced TherapistPage.jsx - SEO-Optimized with Location & Better UI/UX
"use client";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Star,
  ArrowLeft,
  Award,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Briefcase,
  Heart,
  Share2,
  ExternalLink,
} from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import ChatBot from "./ChatBot";
import { API_ENDPOINTS, DEFAULT_FETCH_CONFIG } from "../config/api.config";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";

export default function TherapistPageEnhanced() {
  const { city, therapistName, therapistSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [therapist, setTherapist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTherapist = async () => {
      try {
        setLoading(true);

        let fetchUrl;
        // Handle new route format: /therapist/:city/:therapistName
        if (city && therapistName) {
          // Convert city-therapistName to slug format for API call
          const slug = `${therapistName}`;
          fetchUrl = API_ENDPOINTS.seo.getTherapist(slug);
        }
        // Handle legacy routes: /massage-therapists/:slug or /therapists/:slug
        else if (therapistSlug) {
          fetchUrl = API_ENDPOINTS.seo.getTherapist(therapistSlug);
        } else {
          setError("Invalid therapist URL");
          return;
        }

        const response = await fetch(fetchUrl, DEFAULT_FETCH_CONFIG);

        if (!response.ok) {
          if (response.status === 404) {
            setError("Therapist profile not found");
          } else {
            setError("Failed to load therapist profile");
          }
          return;
        }

        const data = await response.json();
        setTherapist(data);

        // If using legacy route, redirect to new format
        if (therapistSlug && data.city && data.slug) {
          const citySlug = data.city.toLowerCase().replace(/\s+/g, "-");
          navigate(`/therapist/${citySlug}/${data.slug}`, { replace: true });
        }
      } catch (err) {
        console.error("Error fetching therapist:", err);
        setError("Failed to load therapist profile");
      } finally {
        setLoading(false);
      }
    };

    fetchTherapist();
  }, [city, therapistName, therapistSlug, navigate]);

  const handleBookNow = () => {
    if (therapist) {
      // Navigate to booking confirmation with pre-selected therapist
      navigate("/book/confirm", {
        state: {
          selection: {
            therapistId: therapist._id,
            fullName: fullName,
            photoUrl: profilePhotoUrl,
            availableSettings: therapist.availableSettings || therapist.settings || [],
            settingsData: therapist.settingsData,
          },
          context: {
            // Include any context that might be needed
            city: therapistCity,
          },
        },
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share && therapist) {
      try {
        await navigator.share({
          title: `${therapist.fullName} - Massage Therapist`,
          text: `Book a massage with ${therapist.fullName} on BookaTherapy`,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Share failed:", err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#98C454] border-t-transparent mx-auto"></div>
          <p className="mt-6 text-[#252F4F] font-semibold text-lg">
            Loading therapist profile...
          </p>
        </div>
      </div>
    );
  }

  if (error || !therapist) {
    return (
      <div className="min-h-screen bg-white">
        <NavbarDemo />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="max-w-md mx-auto">
            <div className="mb-6 text-6xl">😔</div>
            <h1 className="text-3xl font-bold mb-4 text-[#252F4F]">
              Therapist Not Found
            </h1>
            <p className="text-[#252F4F]/70 mb-8 text-lg">
              {error || "The therapist profile you're looking for doesn't exist."}
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                className="bg-[#98C454] hover:bg-[#87b043] text-white font-semibold px-6"
                onClick={() => navigate("/therapists")}>
                Browse All Therapists
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}>
                Go Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Extract data
  const {
    fullName,
    slug,
    bio,
    bioOneLiner,
    profilePhotoUrl,
    specialties = [],
    city: therapistCity,
    postalCode,
    clinicLocation,
    clinicInfo,
    isAvailable,
    averageRating,
    totalReviews,
    reviews = [],
    seoTitle,
    seoDescription,
    jsonLd,
    yearsOfExperience,
    certifications = [],
  } = therapist;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Render star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 sm:h-5 sm:w-5 ${
          i < Math.floor(rating)
            ? "fill-yellow-400 text-yellow-400"
            : "text-gray-300"
        }`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* SEO Head */}
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={`https://bookatherapy.com/therapist/${therapistCity?.toLowerCase().replace(/\s+/g, "-")}/${slug}`}
        ogImage={profilePhotoUrl}
        ogType="profile"
        schema={jsonLd}
        keywords={`massage therapist ${therapistCity}, massage near me ${postalCode}, book a massage ${therapistCity}, ${fullName} massage therapist, massage therapy ${therapistCity}, RMT ${therapistCity}, registered massage therapist near me`}
      />

      <NavbarDemo />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-4 sm:mb-6 text-[#252F4F] hover:text-[#98C454] text-sm sm:text-base"
          onClick={() => navigate("/therapists")}>
          <ArrowLeft className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
          Back to All Therapists
        </Button>

        {/* Hero Section */}
        <Card className="border-2 border-[#98C454]/20 shadow-xl mb-6 sm:mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-[#252F4F] to-[#98C454] h-24"></div>
          <CardContent className="pt-0 pb-6 px-4 sm:px-6 md:px-8">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6 -mt-12">
              {/* Profile Photo */}
              <Avatar className="h-24 w-24 sm:h-32 sm:w-32 border-4 border-white shadow-xl">
                {profilePhotoUrl ? (
                  <AvatarImage src={profilePhotoUrl} alt={fullName} />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-[#98C454] to-[#87b043] text-white text-2xl sm:text-3xl font-bold">
                  {getInitials(fullName)}
                </AvatarFallback>
              </Avatar>

              {/* Name & Details */}
              <div className="flex-1 mt-10 md:mt-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#252F4F] mb-2">
                      {fullName}
                    </h1>
                    {bioOneLiner && (
                      <p className="text-base sm:text-lg text-[#252F4F]/70 mb-3">
                        {bioOneLiner}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                      {/* Location */}
                      <div className="flex items-center text-[#252F4F]/80 text-sm sm:text-base">
                        <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-[#98C454]" />
                        <span className="font-medium">
                          {therapistCity}
                          {postalCode && `, ${postalCode}`}
                        </span>
                      </div>

                      {/* Rating */}
                      {averageRating > 0 && (
                        <div className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base">
                          <div className="flex">{renderStars(averageRating)}</div>
                          <span className="font-semibold text-[#252F4F]">
                            {averageRating.toFixed(1)}
                          </span>
                          <span className="text-[#252F4F]/60 text-xs sm:text-sm">
                            ({totalReviews} {totalReviews === 1 ? "review" : "reviews"})
                          </span>
                        </div>
                      )}

                      {/* Availability Badge */}
                      {isAvailable && (
                        <Badge className="bg-green-100 text-green-800 border-green-300 text-xs sm:text-sm">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Available Now
                        </Badge>
                      )}

                      {/* Experience */}
                      {yearsOfExperience && (
                        <Badge variant="outline" className="border-[#98C454] text-[#252F4F] text-xs sm:text-sm">
                          <Award className="h-3 w-3 mr-1" />
                          {yearsOfExperience} Years Experience
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <Button
                      size="lg"
                      onClick={handleBookNow}
                      className="bg-[#98C454] hover:bg-[#87b043] text-white font-bold shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                      <Calendar className="mr-2 h-5 w-5" />
                      Book Appointment
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={handleShare}
                      className="border-[#252F4F] text-[#252F4F] hover:bg-[#252F4F] hover:text-white w-full sm:w-auto">
                      <Share2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-start">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 lg:space-y-8">
            {/* About Section */}
            <Card className="border-[#98C454]/20 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl text-[#252F4F] flex items-center gap-2">
                  <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-[#98C454]" />
                  About {fullName.split(" ")[0]}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#252F4F]/80 leading-relaxed text-base sm:text-lg whitespace-pre-line">
                  {bio || bioOneLiner}
                </p>
              </CardContent>
            </Card>

            {/* Specialties */}
            {specialties.length > 0 && (
              <Card className="border-[#98C454]/20 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl sm:text-2xl text-[#252F4F] flex items-center gap-2">
                    <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-[#98C454]" />
                    Specialties & Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {specialties.map((specialty, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-[#98C454]/10 to-[#98C454]/5 border border-[#98C454]/20">
                        <CheckCircle className="h-5 w-5 text-[#98C454] flex-shrink-0" />
                        <span className="text-[#252F4F] font-medium">{specialty}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {certifications.length > 0 && (
              <Card className="border-[#98C454]/20 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl sm:text-2xl text-[#252F4F] flex items-center gap-2">
                    <Award className="h-5 w-5 sm:h-6 sm:w-6 text-[#98C454]" />
                    Certifications & Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {certifications.map((cert, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-[#98C454] mt-0.5 flex-shrink-0" />
                        <span className="text-[#252F4F]/80">{cert}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            {reviews.length > 0 && (
              <Card className="border-[#98C454]/20 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl sm:text-2xl text-[#252F4F] flex items-center gap-2">
                    <Star className="h-5 w-5 sm:h-6 sm:w-6 text-[#98C454]" />
                    Client Reviews
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {reviews.map((review, index) => (
                      <div key={index} className="border-l-4 border-[#98C454] pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">{renderStars(review.rating)}</div>
                          <span className="font-semibold text-[#252F4F]">
                            {review.clientName || "Anonymous"}
                          </span>
                        </div>
                        <p className="text-[#252F4F]/70 italic">"{review.comment}"</p>
                        {review.date && (
                          <p className="text-sm text-[#252F4F]/50 mt-2">
                            {new Date(review.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Book Appointment Card */}
            <Card className="border-2 border-[#98C454] shadow-xl">
              <CardContent className="p-4 sm:p-6 space-y-4">
                <h3 className="text-lg sm:text-xl font-bold text-[#252F4F]">
                  Ready to Book?
                </h3>
                <p className="text-sm sm:text-base text-[#252F4F]/70">
                  Schedule your appointment with {fullName.split(" ")[0]} today
                </p>
                <Button
                  onClick={handleBookNow}
                  className="w-full bg-[#98C454] hover:bg-[#87b043] text-white font-bold py-5 sm:py-6 text-base sm:text-lg shadow-lg">
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Now
                </Button>
                {isAvailable && (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="h-4 w-4" />
                    <span>Available for bookings</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Clinic Location */}
            {clinicLocation && (
              <Card className="border-[#98C454]/20 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg text-[#252F4F] flex items-center gap-2">
                    <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-[#98C454]" />
                    Practice Location
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {clinicInfo?.name && (
                    <p className="font-semibold text-[#252F4F]">
                      {clinicInfo.name}
                    </p>
                  )}
                  {clinicLocation.address && (
                    <p className="text-[#252F4F]/70 text-sm">
                      {clinicLocation.address}
                    </p>
                  )}
                  {clinicInfo?.phone && (
                    <div className="flex items-center gap-2 text-[#252F4F]/70">
                      <Phone className="h-4 w-4 text-[#98C454]" />
                      <a
                        href={`tel:${clinicInfo.phone}`}
                        className="hover:text-[#98C454] transition-colors">
                        {clinicInfo.phone}
                      </a>
                    </div>
                  )}
                  {clinicInfo?.email && (
                    <div className="flex items-center gap-2 text-[#252F4F]/70">
                      <Mail className="h-4 w-4 text-[#98C454]" />
                      <a
                        href={`mailto:${clinicInfo.email}`}
                        className="hover:text-[#98C454] transition-colors text-sm">
                        {clinicInfo.email}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <ChatBot />
    </div>
  );
}
